// Reusable Feature tabs / accordion display.
//   Desktop (md+): horizontal scrollable tab strip + content panel below.
//   Mobile (<md): vertical accordion (one item open at a time).
//
// Props:
//   items     : Array<{ key, Icon, title, desc, bullets?: string[], image?: string, href?: string, hrefLabel?: string }>
//   eyebrow?  : string  shown above the heading
//   heading?  : ReactNode
//   subheading?: ReactNode
//   variant?  : "light" | "dark"   visual surface (default "light")
//   ctaLabel? : string  override for "Learn more" link label
//   testId?   : string  data-testid on the root container
//
// The component is purely client-side and stateful (no router required).

import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ChevronDown } from "lucide-react";

export default function FeatureTabs({
  items = [],
  eyebrow,
  heading,
  subheading,
  variant = "light",
  ctaLabel = "Learn more",
  testId = "feature-tabs",
}) {
  const [activeKey, setActiveKey] = useState(items[0]?.key || null);
  const [openMobile, setOpenMobile] = useState(items[0]?.key || null);

  if (!items.length) return null;

  const active = items.find((i) => i.key === activeKey) || items[0];
  const isDark = variant === "dark";

  const surface = isDark
    ? "bg-[hsl(207_74%_21%)] text-white border border-white/10"
    : "bg-white text-primary border border-border";
  const labelColor = isDark ? "text-white/70" : "text-muted-foreground";

  return (
    <section data-testid={testId} className="py-16 md:py-20">
      <div className="container-cv">
        {(eyebrow || heading || subheading) && (
          <div className="max-w-3xl mb-10">
            {eyebrow && <span className="section-eyebrow">{eyebrow}</span>}
            {heading && (
              <h2 className="mt-3 font-display text-3xl md:text-5xl font-bold text-primary tracking-tight leading-[1.08]">
                {heading}
              </h2>
            )}
            {subheading && (
              <p className="mt-4 text-base md:text-lg text-muted-foreground">{subheading}</p>
            )}
          </div>
        )}

        {/* ===== Desktop: tab strip + content ===== */}
        <div className="hidden md:block">
          <div
            role="tablist"
            aria-label="Feature tabs"
            className="flex flex-wrap gap-2 border-b border-border pb-px"
          >
            {items.map((it) => {
              const isActive = it.key === activeKey;
              return (
                <button
                  key={it.key}
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setActiveKey(it.key)}
                  data-testid={`${testId}-tab-${it.key}`}
                  className={`group inline-flex items-center gap-2 px-4 py-3 -mb-px border-b-2 transition-all ${
                    isActive
                      ? "border-accent text-primary"
                      : "border-transparent text-muted-foreground hover:text-primary"
                  }`}
                >
                  {it.Icon && (
                    <span
                      className={`grid place-items-center h-8 w-8 rounded-lg transition-colors ${
                        isActive
                          ? "bg-accent text-primary"
                          : "bg-primary/5 text-primary group-hover:bg-primary/10"
                      }`}
                    >
                      <it.Icon className="h-4 w-4" />
                    </span>
                  )}
                  <span className="text-sm font-bold tracking-tight whitespace-nowrap">
                    {it.title}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Content panel */}
          <div
            role="tabpanel"
            className={`mt-8 rounded-2xl p-8 md:p-10 ${surface}`}
            data-testid={`${testId}-panel-${active.key}`}
          >
            <div className={`grid gap-8 ${active.image ? "lg:grid-cols-12 items-center" : ""}`}>
              <div className={active.image ? "lg:col-span-6" : "max-w-3xl"}>
                <h3
                  className={`font-display text-2xl md:text-3xl font-bold tracking-tight ${
                    isDark ? "text-white" : "text-primary"
                  }`}
                >
                  {active.title}
                </h3>
                <p className={`mt-4 text-base leading-relaxed ${labelColor}`}>{active.desc}</p>

                {active.bullets?.length > 0 && (
                  <ul className="mt-5 space-y-2.5">
                    {active.bullets.map((b, i) => (
                      <li
                        key={i}
                        className={`flex items-start gap-2.5 text-sm ${
                          isDark ? "text-white/80" : "text-primary/90"
                        }`}
                      >
                        <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-accent shrink-0" />
                        {b}
                      </li>
                    ))}
                  </ul>
                )}

                {active.href && (
                  <Link
                    to={active.href}
                    data-testid={`${testId}-cta-${active.key}`}
                    className={`mt-7 inline-flex items-center gap-1.5 text-sm font-bold ${
                      isDark ? "text-accent" : "text-primary hover:text-accent"
                    } transition-colors`}
                  >
                    {active.hrefLabel || ctaLabel}
                    <ArrowUpRight className="h-4 w-4" />
                  </Link>
                )}
              </div>

              {active.image && (
                <div className="lg:col-span-6">
                  <div className="rounded-xl overflow-hidden border border-border/60 bg-secondary/30">
                    <img
                      src={active.image}
                      alt={active.title}
                      loading="lazy"
                      className="w-full h-auto object-cover"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ===== Mobile: accordion ===== */}
        <div className="md:hidden space-y-3">
          {items.map((it) => {
            const isOpen = it.key === openMobile;
            return (
              <div
                key={it.key}
                className="rounded-xl border border-border bg-white overflow-hidden"
              >
                <button
                  onClick={() => setOpenMobile(isOpen ? null : it.key)}
                  data-testid={`${testId}-acc-${it.key}`}
                  className="w-full flex items-center justify-between gap-3 px-4 py-4 text-left"
                  aria-expanded={isOpen}
                >
                  <span className="flex items-center gap-3 min-w-0">
                    {it.Icon && (
                      <span className="grid place-items-center h-8 w-8 rounded-lg bg-primary/5 text-primary shrink-0">
                        <it.Icon className="h-4 w-4" />
                      </span>
                    )}
                    <span className="font-display font-bold text-primary text-sm tracking-tight">
                      {it.title}
                    </span>
                  </span>
                  <ChevronDown
                    className={`h-4 w-4 text-muted-foreground transition-transform ${
                      isOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {isOpen && (
                  <div className="px-4 pb-5">
                    <p className="text-sm text-muted-foreground leading-relaxed">{it.desc}</p>
                    {it.bullets?.length > 0 && (
                      <ul className="mt-3 space-y-1.5">
                        {it.bullets.map((b, i) => (
                          <li
                            key={i}
                            className="flex items-start gap-2 text-xs text-primary/85"
                          >
                            <span className="mt-1 h-1.5 w-1.5 rounded-full bg-accent shrink-0" />
                            {b}
                          </li>
                        ))}
                      </ul>
                    )}
                    {it.image && (
                      <div className="mt-4 rounded-lg overflow-hidden border border-border/60">
                        <img src={it.image} alt={it.title} loading="lazy" className="w-full h-auto" />
                      </div>
                    )}
                    {it.href && (
                      <Link
                        to={it.href}
                        className="mt-4 inline-flex items-center gap-1 text-xs font-bold text-primary hover:text-accent transition-colors"
                      >
                        {it.hrefLabel || ctaLabel}
                        <ArrowUpRight className="h-3.5 w-3.5" />
                      </Link>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
